'use client';

import { getClasses } from '@/_utils/classnames';
import { useContext, useEffect, type FC } from 'react';
import styles from './LoginForm.module.css';
import {
  Card,
  Flex,
  Text,
  SetSignInMethod,
  SetEmail,
  SetPassword,
  SetPhoneNumber,
  TwoFactorAuth,
} from '@/_components';
import { MaintenanceBanner } from '@/_components/MaintenanceBanner';
import { Login } from './components/Login';
import { motion } from 'framer-motion';
import { AlternateSignIn } from './components/AlternateSignIn';
import { ExpiredLink } from './components/ExpiredLink';
import { ForgotPassword } from './components/ForgotPassword';
import { SentCode } from './components/SentCode';
import { SentLink } from './components/SentLink';
import { TemplateStart } from './components/TemplateStart';
import { TemplateSignUp } from './components/TemplateSignUp';
import { LoginContext, type LoginFormState } from '@/_contexts/LoginProvider';
import { TemplateSignUpInContext } from '@/_contexts/TemplateSignUpInProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import type { LoginFormProps } from './LoginForm.types';
import { signedOutWithinLastNSeconds } from '@/_utils/auth';
import Link from 'next/link';
import {
  getLocalStorageItem,
  getSessionStorageItem,
  removeLocalStorageItem,
} from '@/_utils/localstorage';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const LoginForm: FC<LoginFormProps> = () => {
  const { loginState } = useContext(LoginContext);
  const { addToast, resetToasts } = useContext(ToastContext);
  const { firmTheme } = useContext(FirmSettingsContext);
  const { firm } = useContext(TemplateSignUpInContext);

  const currentUrl: string =
    typeof window !== 'undefined' ? window?.location?.href : '';
  const shouldShowSorabanStatus: boolean = currentUrl.includes('soraban.com');

  useEffect(() => {
    if (signedOutWithinLastNSeconds(10) && loginState === 'login') {
      const sessionStorageWindowId = getSessionStorageItem('windowId');
      const signedOutWindowId = getLocalStorageItem('signedOutWindowId');

      if (
        !sessionStorageWindowId ||
        sessionStorageWindowId !== signedOutWindowId
      ) {
        // This toast is being added here because if another tab was in the middle of requests, and a signOut is performed in this tab, the other tab would have been signed out but it would have also forced a refresh via window.location.href and that would have removed the toast. So this is here to make sure the user knows they were signed out.
        addToast(
          'info',
          'You have been signed out. When you are signed out in one page, we sign you out in all of them to keep your data secure.',
          {
            doesNotAutoClose: true,
            onClose: () => {
              removeLocalStorageItem('signOut');
            },
          },
        );
      }

      setTimeout(() => {
        removeLocalStorageItem('signOut');
        removeLocalStorageItem('signedOutWindowId');
      }, 10000);
    } else {
      removeLocalStorageItem('signOut');
      removeLocalStorageItem('signedOutWindowId');
      resetToasts();
    }
  }, [addToast, loginState, resetToasts]);

  useEffect(() => {
    return () => {
      resetToasts();
    };
  }, [resetToasts]);

  const loginFormClasses: string = getClasses(styles.loginForm);

  const ActiveComponent: Record<LoginFormState, React.ReactNode> = {
    '2fa': <TwoFactorAuth />,
    'alternate-sign-in': <AlternateSignIn />,
    'expired-link': <ExpiredLink />,
    'forgot-password': <ForgotPassword />,
    'sent-email': <SentCode method="email" />,
    'sent-text': <SentCode method="text" />,
    'sent-link': <SentLink />,
    'set-email': <SetEmail />,
    'set-password': <SetPassword />,
    'set-phone-number': <SetPhoneNumber />,
    'set-sign-in-method': <SetSignInMethod isStandalone={false} />,
    'template-sign-up': <TemplateSignUp />,
    'template-start': <TemplateStart />,
    login: <Login />,
  };

  const templateSignUpInTitle: string =
    firm?.name || firmTheme?.name || 'Loading…';

  const chooseHeader = () => {
    switch (loginState) {
      case '2fa':
        return '2-Factor Auth';
      case 'expired-link':
        return 'One more step to go…';
      case 'forgot-password':
        return 'Reset password';
      case 'set-email':
        return 'Sign in via email';
      case 'set-password':
        return 'Set up password';
      case 'set-phone-number':
        return 'Sign in via SMS';
      case 'set-sign-in-method':
        return 'How do you want to sign in next time?';
      case 'template-start':
        return templateSignUpInTitle;
      case 'template-sign-up':
        return templateSignUpInTitle;
      default:
        return 'Sign In';
    }
  };

  return (
    <motion.div
      initial={{ zoom: 0.95, opacity: 0 }}
      animate={{ zoom: 1, opacity: 1 }}
    >
      <div className={styles.loginFormContainer}>
        <MaintenanceBanner />
        {firmTheme?.url && (
          <Flex className={styles.loginFirm__logo} isCentered={true}>
            <img
              src={firmTheme?.url}
              alt={`${firmTheme?.name || 'Firm'} logo`}
            />
          </Flex>
        )}
        <Card
          className={loginFormClasses}
          elevation="xl"
          header={chooseHeader()}
          data-testid={
            loginState === 'login' ? 'LoginForm-sign-in-card' : undefined
          }
        >
          {ActiveComponent[loginState]}
        </Card>
        <Flex isCentered={true} className={styles.loginFirm__help} gap={4}>
          <Text type="small" color="secondary">
            Trouble signing in? <Link href="/help">Get Help</Link>
          </Text>
          {shouldShowSorabanStatus && (
            <>
              <Text type="small" color="disabled">
                |
              </Text>
              <Text type="small" color="secondary">
                <a href="https://status.soraban.com/">Soraban Status</a>
              </Text>
            </>
          )}
        </Flex>
      </div>
    </motion.div>
  );
};
